/**
 * Asking for a rating, once, at a moment the player is actually pleased.
 *
 * Play's in-app review sheet opens over the game: no trip to the store, no
 * lost session. Play decides whether to actually show it (there is a quota
 * per user per year and it is deliberately undocumented), so the only thing
 * this file controls is WHEN we ask. Everything here exists to make that
 * moment a good one and to never ask twice.
 *
 * The rules Play sets, which are also the honest ones:
 *   Never ask for a rating in exchange for anything.
 *   Never ask for a specific score, or ask "do you like it" first and route
 *   only the happy answers to the sheet.
 *   Never interrupt play. The result screen is over, the score has landed.
 *
 * The two triggers are the two moments the game produces real satisfaction:
 * a clean sweep, and a streak that took several days to build. Neither is
 * asked inside a player's first three games (see FIRST_GAMES_WITHOUT_ASK).
 */
import { InAppReview } from '@capacitor-community/in-app-review';
import { isNative } from '../utils/platform';
import { gamesCompleted } from './pmAd';

const ASKED_KEY = 'wot_review_asked_v1';

/**
 * No ask in the first three finished games. They are dealt easy on purpose,
 * and since 2026-10-04 nearly half of new players finish the first one
 * without a miss, so a sweep there says more about the deck than about the
 * player. An ask spent that early also locks out the better moments for half
 * a year. The same three games stay free of the PsycheMatrix ad (lib/pmAd.ts).
 */
const FIRST_GAMES_WITHOUT_ASK = 3;

/** Play may silently no-op, so the gap is generous: ask again at most twice a year. */
const ASK_AGAIN_AFTER_DAYS = 180;

function askedRecently(): boolean {
  try {
    const raw = localStorage.getItem(ASKED_KEY);
    if (!raw) return false;
    const when = parseInt(raw, 10);
    if (!Number.isFinite(when)) return false;
    return Date.now() - when < ASK_AGAIN_AFTER_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
}

function markAsked(): void {
  try { localStorage.setItem(ASKED_KEY, String(Date.now())); } catch { /* private mode: ask again next time */ }
}

/** The current Daily streak, in days. The Daily screen owns this record. */
function dailyStreak(): number {
  try {
    return JSON.parse(localStorage.getItem('wot_daily_v1') || 'null')?.streak ?? 0;
  } catch {
    return 0;
  }
}

export interface ReviewMoment {
  mode: string | null;      // the match's mode, DAILY included
  correct: number;
  total: number;
  isMultiplayer: boolean;
}

/**
 * True when this result is worth a rating prompt: a perfect solo round, or a
 * Daily streak that has survived three days, once the first three games are
 * behind the player. Multiplayer results are left alone, because half of
 * them end in a loss and the other half are one tap from a rematch.
 */
export function isReviewMoment(m: ReviewMoment): boolean {
  if (m.isMultiplayer) return false;
  if (gamesCompleted() <= FIRST_GAMES_WITHOUT_ASK) return false;
  if (m.total >= 5 && m.correct === m.total) return true;
  if (m.mode === 'DAILY' && dailyStreak() >= 3) return true;
  return false;
}

/**
 * Ask Play to show its review sheet. Native only: the web and the itch embed
 * have no store to rate in. Failures are silent by design, because a player
 * who was not shown a sheet should never learn that one was attempted.
 */
export async function requestReview(m: ReviewMoment): Promise<void> {
  if (!isNative) return;
  if (!isReviewMoment(m)) return;
  if (askedRecently()) return;

  markAsked();
  try {
    await InAppReview.requestReview();
  } catch {
    /* no store, no Play services, quota spent: none of it is the player's problem */
  }
}
